import { ampli } from '~/domain/plugins/analytics';
import { describeRoute } from '~/lib/routing/route-registry';
import { clientLogger } from '~/lib/logger';

/**
 * The header logo is a link, and for most companies it points at their OWN
 * website — so clicking it is a visitor leaving the storefront, from any page
 * including checkout. Nothing recorded that.
 *
 * The gap mattered because the other exits are instrumented: the checkout
 * header's cross fires `Checkout Dismissed`, so the funnel looked like it had
 * one measurable way out when it had two. Amplitude's `elementInteractions`
 * autocapture did pick these clicks up incidentally between 8 Jun and 17 Aug
 * 2026, then #1122 turned it off as unused — which is exactly why this belongs
 * in a deliberate event rather than in autocapture nobody owns.
 *
 * The event itself lives in the Amplitude tracking plan (`Header Company
 * Website Clicked`, category Header, source storefront-web), so the call below
 * is the generated typed method like every other product event here. This
 * wrapper exists for what the plan cannot express: deriving the properties from
 * the DOM, flushing before the document unloads, and never letting analytics
 * break the navigation.
 */

/**
 * Records a header logo click, with the page it happened on.
 *
 * `page_name`/`page_path_pattern` are derived the same way the Page Viewed
 * enrichment derives them (`describeRoute`, the routing registry being the
 * single source of truth), because the enrichment only stamps Page Viewed and
 * the checkout enrichment only stamps `Checkout *` events — this event is
 * neither, and "which page did they leave from" is the whole question.
 */
export function trackHeaderCompanyWebsiteClicked(destination: string): void {
  // Analytics must never break navigation: the browser is already leaving.
  try {
    // Before Amplitude has loaded the event would only be held, and this
    // document is about to unload: it could never be sent.
    if (!ampli.isLoaded) {
      return;
    }

    const target = new URL(destination, window.location.href);
    const { pattern, name } = describeRoute(window.location.pathname);

    ampli.headerCompanyWebsiteClicked({
      // The host, not the full URL: it answers "where did they go" without
      // carrying query strings or per-visitor path ids into the event.
      destination_host: target.host,
      // The point of the event. False means the logo behaved as "home" —
      // the company has no website recorded, so the link falls back to `/`.
      is_external: target.host !== window.location.host,
      page_name: name,
      page_path_pattern: pattern,
    });

    // The click is about to unload the document, so ask the SDK to send now
    // rather than on its next batch tick. Best-effort by nature: an in-flight
    // request can still be cancelled by the navigation, in which case the SDK
    // keeps the event in storage and sends it on this visitor's next arrival.
    void ampli.client?.flush();
  } catch (error) {
    clientLogger.warn(error, '[amplitude] header website click not tracked');
  }
}
